import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, asArray, formatApiError } from "@/lib/api";
import { pesoOr } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";
import { fmtTime } from "@/lib/time";
import { renderUntrustedHtml } from "@/lib/markdown";
import {
  Inbox, Clock3, ListChecks, Coins, Plus, Pencil, Trash2, X, Save, MessageSquareQuote,
  Mail, Paperclip, UserPlus, Ban, Loader2, Search, Star,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// A technician who cannot manage shared snippets never sees a working "Share
// this" toggle -- but that is a courtesy only. The server refuses a shared
// create/edit from anyone outside this list independently of what the UI
// shows, so a stale role check here degrades to "the button did nothing
// unexpected", never to "the API let it through".
const MANAGE_SHARED_ROLES = ["service_desk_manager", "l3_technician", "super_admin", "msp_owner"];

// Mirrors backend/mailintake/pipeline.py's TECH_ROLES plus the two roles
// require_roles() always admits (super_admin/msp_owner pass every check
// regardless of the named list). This is a courtesy hide only -- every
// /api/mail/quarantine* route re-checks the identical set server-side, and a
// stale copy here can only make the tab disappear, never make a route answer
// for a role that shouldn't reach it.
const MAIL_ROLES = ["service_desk_manager", "l1_technician", "l2_technician", "l3_technician", "super_admin", "msp_owner"];

// Six endpoints that never had a screen. PUT /api/desk/sla has accepted a
// client_id all along and nothing has ever sent one -- per-client service
// levels and business calendars, the engine's best feature, were unreachable.
// Queues could not be created, templates could not be authored.
//
// The server is the gate, not this page: every write below hits
// require_roles(service_desk_manager, l3_technician, msp_owner, super_admin)
// and a 403 from any of them is expected to render as a readable toast, not a
// crash -- Layout.jsx hides the nav entry for anyone else, but that is a door
// removed, not a lock installed.

const PRIORITIES = ["critical", "high", "medium", "low"];
// Matches backend/psa.py's TIERS and Clients.jsx's own edit dropdown --
// the only three values `clients.tier` is ever set to.
const SLA_TIERS = ["Standard", "Premium", "Enterprise"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Everything on this page EXCEPT Mail is desk configuration -- queues, SLA
// policies, templates, and the billing rates on work types. Mail is
// front-line triage, which is an l1/l2 job. Before this list carried
// CONFIG_ROLES the two were inseparable: the only nav link here is gated to
// managers and l3, so l1 and l2 could not reach the Mail tab written for
// them, and the obvious fix -- widening that one nav entry -- would have
// handed them the SLA policies and the rate card as well.
//
// These `roles` are UX, never the control: each underlying route carries its
// own server-side guard, and a stale copy here can only hide a tab, never
// make a route answer for a role that should not reach it.
const CONFIG_ROLES = ["service_desk_manager", "l3_technician", "super_admin", "msp_owner"];

const TABS = [
  { key: "queues", label: "Queues", icon: Inbox, roles: CONFIG_ROLES },
  { key: "mail", label: "Mail", icon: Mail, roles: MAIL_ROLES },
  { key: "sla", label: "SLA", icon: Clock3, roles: CONFIG_ROLES },
  { key: "templates", label: "Templates", icon: ListChecks, roles: CONFIG_ROLES },
  { key: "worktypes", label: "Work types", icon: Coins, roles: CONFIG_ROLES },
  { key: "canned", label: "Canned responses", icon: MessageSquareQuote, roles: CONFIG_ROLES },
  { key: "vip", label: "VIP contacts", icon: Star, roles: CONFIG_ROLES },
];

const inputCls = "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-blue-500/60";
const labelCls = "text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1 block";

export default function ServiceDesk() {
  const { user } = useAuth();
  // Land on the tab the URL asks for (the sidebar's Mail entry links to
  // ?tab=mail), else the first tab this role may actually see -- "queues"
  // as a hardcoded default would have shown an l1 an empty page.
  const visibleTabs = TABS.filter((t) => !t.roles || (user && t.roles.includes(user.role)));
  const wanted = new URLSearchParams(window.location.search).get("tab");
  const [tab, setTab] = useState(
    visibleTabs.some((t) => t.key === wanted) ? wanted : (visibleTabs[0]?.key || "mail"));
  const [mailPending, setMailPending] = useState(null);
  const mayViewMail = !!user && MAIL_ROLES.includes(user.role);

  // Fetched here (not only inside MailTab) so the badge is right on the tab
  // button even before that tab has ever been opened, and stays put in
  // component state while a different tab is mounted. A role this hides the
  // tab from is never asked -- the count would just come back 403.
  const refreshMailPending = useCallback(() => {
    if (!mayViewMail) return;
    api.get("/mail/quarantine", { params: { state: "pending", limit: 1 } })
      .then((r) => setMailPending(Number(r.data?.total) || 0))
      .catch(() => setMailPending(null));
  }, [mayViewMail]);
  useEffect(() => { refreshMailPending(); }, [refreshMailPending]);

  return (
    <div className="space-y-4" data-testid="service-desk-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Service Desk · Configuration</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Service Desk Config</h1>
        <div className="text-xs text-zinc-500 mt-1">Queues, service levels, templates and billable rates.</div>
      </div>

      <div className="flex gap-1 border-b border-zinc-900">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`sdconfig-tab-${t.key}`}
              className={`flex items-center gap-2 px-3.5 py-2 text-[13px] border-b-2 -mb-px transition-colors ${
                on ? "border-blue-500 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              <Icon size={14} /> {t.label}
              {t.key === "mail" && !!mailPending && (
                <span data-testid="mail-pending-badge"
                  className="ml-0.5 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full text-[10px] font-mono font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  {mailPending}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "queues" && <QueuesTab />}
      {tab === "mail" && mayViewMail && <MailTab onPendingCount={setMailPending} />}
      {tab === "sla" && <SlaTab />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "worktypes" && <WorkTypesTab />}
      {tab === "canned" && <CannedResponsesTab />}
      {tab === "vip" && <VipTab />}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Queues
// --------------------------------------------------------------------------- //
const EMPTY_QUEUE = { name: "", categories: "", member_ids: [], auto_assign: true, is_default: false };

function QueuesTab() {
  const [queues, setQueues] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dialog, setDialog] = useState(null); // { editing: queue|null, form }

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/desk/queues"),
      api.get("/desk/workload").catch(() => ({ data: { technicians: [] } })),
    ]).then(([q, w]) => {
      setQueues(asArray(q.data));
      setTechnicians(asArray(w.data?.technicians));
      setErr("");
    }).catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setDialog({ editing: null, form: { ...EMPTY_QUEUE } });
  const openEdit = (q) => setDialog({
    editing: q,
    form: { name: q.name, categories: (q.categories || []).join(", "), member_ids: q.member_ids || [],
            auto_assign: !!q.auto_assign, is_default: !!q.is_default },
  });

  const save = async () => {
    const { editing, form } = dialog;
    const payload = {
      name: form.name.trim(),
      categories: form.categories.split(",").map((c) => c.trim()).filter(Boolean),
      member_ids: form.member_ids,
      auto_assign: form.auto_assign,
      is_default: form.is_default,
    };
    if (!payload.name) { toast.error("Name is required."); return; }
    try {
      if (editing) await api.put(`/desk/queues/${editing.id}`, payload);
      else await api.post("/desk/queues", payload);
      toast.success(editing ? "Queue updated" : "Queue created");
      setDialog(null);
      load();
    } catch (e) {
      // 409s here are refusals to act on, not generic failures: the default
      // queue cannot be deleted/demoted, and category routing depends on a
      // second queue existing at all -- surface the server's own wording.
      toast.error(formatApiError(e));
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete queue "${q.name}"?`)) return;
    try {
      await api.delete(`/desk/queues/${q.id}`);
      toast.success("Queue deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={openCreate} data-testid="queue-create-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5">
          <Plus size={15} /> New queue
        </button>
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>}
      {loading && <div className="text-zinc-600 text-sm">Loading…</div>}
      {!loading && !err && !queues.length && <div className="text-zinc-600 text-sm">No queues yet.</div>}

      <div className="grid gap-3 md:grid-cols-2" data-testid="queue-list">
        {queues.map((q) => (
          <div key={q.id} data-testid={`queue-row-${q.id}`}
            className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-zinc-100 font-medium truncate">{q.name}</span>
                  {q.is_default && (
                    <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border bg-blue-500/10 text-blue-300 border-blue-500/30">Default</span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  {q.categories?.length ? q.categories.join(", ") : "No categories — matches nothing directly"}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openEdit(q)} title="Edit" data-testid={`queue-edit-${q.id}`}
                  className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"><Pencil size={14} /></button>
                <button onClick={() => remove(q)} title="Delete" data-testid={`queue-delete-${q.id}`}
                  className="p-1.5 rounded hover:bg-zinc-800 text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
              <span>Members: <span className="font-mono text-zinc-300">{q.member_ids?.length || 0}</span></span>
              <span>Auto-assign: <span className="font-mono text-zinc-300">{q.auto_assign ? "on" : "off"}</span></span>
              <span>Open tickets: <span className="font-mono text-zinc-300">{q.open_tickets ?? "—"}</span></span>
            </div>
          </div>
        ))}
      </div>

      {dialog && (
        <Dialog open onOpenChange={(o) => { if (!o) setDialog(null); }}>
          <DialogContent data-testid="queue-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">{dialog.editing ? "Edit queue" : "New queue"}</DialogTitle>
              <DialogDescription className="sr-only">Configure the queue name, categories and routing.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="block">
                <span className={labelCls}>Name</span>
                <input value={dialog.form.name} data-testid="queue-name"
                  onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, name: e.target.value } }))}
                  className={inputCls} autoFocus />
              </label>
              <label className="block">
                <span className={labelCls}>Categories (comma-separated)</span>
                <input value={dialog.form.categories} data-testid="queue-categories"
                  onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, categories: e.target.value } }))}
                  placeholder="Hardware, Network"
                  className={inputCls} />
                <div className="text-[11px] text-zinc-600 mt-1">A ticket whose category matches one of these routes here; unmatched tickets go to the default queue.</div>
              </label>
              <div>
                <span className={labelCls}>Members</span>
                <div className="max-h-32 overflow-y-auto border border-zinc-800 rounded p-2 space-y-1">
                  {!technicians.length && <div className="text-xs text-zinc-600">No technicians found.</div>}
                  {technicians.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" className="accent-blue-500"
                        checked={dialog.form.member_ids.includes(t.id)}
                        onChange={(e) => setDialog((d) => {
                          const ids = e.target.checked
                            ? [...d.form.member_ids, t.id]
                            : d.form.member_ids.filter((id) => id !== t.id);
                          return { ...d, form: { ...d.form, member_ids: ids } };
                        })} />
                      {t.name} <span className="text-zinc-600 text-xs">({t.open_tickets} open)</span>
                    </label>
                  ))}
                </div>
                <div className="text-[11px] text-zinc-600 mt-1">Empty = any active technician is eligible for assignment.</div>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" className="accent-blue-500" checked={dialog.form.auto_assign}
                  onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, auto_assign: e.target.checked } }))} />
                Auto-assign to the least-loaded member
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" className="accent-blue-500" checked={dialog.form.is_default}
                  onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, is_default: e.target.checked } }))} />
                Default queue (a tenant must always have exactly one)
              </label>
            </div>
            <DialogFooter>
              <button onClick={() => setDialog(null)} className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
              <button onClick={save} data-testid="queue-save"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded px-4 py-1.5 text-sm flex items-center gap-1.5">
                <Save size={14} /> Save
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Mail quarantine
// --------------------------------------------------------------------------- //
// `From` is trivially forged. Mail arriving from a sender this tenant does
// not already recognise is quarantined -- stored, never attached to any
// client's tickets -- until a human decides where it belongs. This tab is
// that decision.

// Business-hours age comes from the SERVER (`business_hours_old` on each
// row from GET /api/mail/quarantine) rather than being recomputed here.
// This used to be a second, hand-rolled Mon-Fri 08:00-18:00 +08:00 walk that
// agreed with `alerting.cond_mail_quarantine_age` only by coincidence, on
// today's default calendar -- the moment an operator edits the SLA calendar
// (the holiday editor is in this very file, in SlaTab below), this screen
// and that alert would start answering a different number for the same row.
// The backend computes it with the identical `psa.calendar_for` +
// `psa.business_minutes_between` calls the alert makes, so the two cannot
// disagree by construction.

// Human age off the wall clock ("received 2d ago" stays true over a
// weekend) -- only the amber threshold is business-hours-aware, and that
// threshold is the server's number, not this one.
function mailHumanAge(iso) {
  const start = new Date(iso);
  if (isNaN(start.getTime())) return null;
  const ms = Date.now() - start.getTime();
  if (ms < 0) return "0m";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
}

function MailAge({ receivedAt, businessHoursOld }) {
  const text = mailHumanAge(receivedAt);
  if (businessHoursOld == null || text == null) return <span className="text-zinc-500">—</span>;
  const stale = businessHoursOld > 24;
  return (
    <span className={stale ? "text-amber-400 font-medium" : "text-zinc-400"}
      title={`${businessHoursOld.toFixed(1)} business hour(s) old${stale ? " -- past 24" : ""}`}>
      {text}
    </span>
  );
}

function mailSenderLabel(q) {
  const addr = q.from_addr || "(unknown sender)";
  return q.from_name ? `${q.from_name} <${addr}>` : addr;
}

function MailTab({ onPendingCount }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  // `/api/mail/inbound/status` existed, was tested, and NOTHING called it.
  // Meanwhile the empty state below promised "unrecognised senders will
  // land here", which on a deployment with no Entra credentials is simply
  // untrue -- nothing will ever land there until Graph is configured, and a
  // manager reading a reassuring empty list concludes intake works and
  // nobody has written in. The endpoint that says otherwise is the one
  // route in this sub-project that had no door.
  const [ready, setReady] = useState(null);
  const [convertFor, setConvertFor] = useState(null);
  const [attachFor, setAttachFor] = useState(null);
  const [contactFor, setContactFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/mail/inbound/status")
      .then((r) => setReady(r.data))
      .catch(() => setReady(null));
    api.get("/mail/quarantine", { params: { state: "pending", limit: 100 } })
      .then((r) => {
        const list = asArray(r.data?.items);
        setItems(list);
        setErr("");
        onPendingCount?.(Number(r.data?.total) || 0);
      })
      .catch((e) => { setItems([]); setErr(formatApiError(e)); onPendingCount?.(null); })
      .finally(() => setLoading(false));
  }, [onPendingCount]);
  useEffect(() => { load(); }, [load]);

  // Called from each action's SUCCESS handler, after the server has already
  // confirmed the state change -- this is confirm-then-splice, not an
  // optimistic update with something to reconcile if the request fails.
  // It exists only to avoid a full reload after every single resolution: a
  // technician working the queue row by row would otherwise re-fetch the
  // whole list on every discard.
  const settle = (qid) => {
    setItems((prev) => prev.filter((q) => q.id !== qid));
    onPendingCount?.((prevTotal) => (typeof prevTotal === "number" ? Math.max(0, prevTotal - 1) : prevTotal));
  };

  const discard = async (q) => {
    if (!window.confirm(`Discard mail from ${mailSenderLabel(q)}? This cannot be undone.`)) return;
    setBusyId(q.id);
    try {
      await api.post(`/mail/quarantine/${q.id}/discard`);
      toast.success("Discarded");
      settle(q.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="mail-tab">
      <div className="text-xs text-zinc-500">
        Mail from a sender this tenant does not already recognise. Nothing here is attached to any
        client's tickets until a person makes that call.
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>}
      {loading && <div className="text-zinc-600 text-sm">Loading…</div>}
      {!loading && !err && !items.length && (
        ready && ready.ready === false ? (
          <div className="text-sm text-amber-400/90 border border-amber-500/30 bg-amber-500/5 rounded p-3"
            data-testid="mail-not-configured">
            <div className="font-medium">Inbound mail is not configured, so nothing can arrive here yet.</div>
            <ul className="mt-1.5 text-zinc-400 text-xs list-disc list-inside space-y-0.5">
              {(ready.checks || []).filter((c) => !c.ok).map((c) => (
                <li key={c.key}><span className="font-mono">{c.key}</span> &mdash; {c.detail}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-zinc-600 text-sm" data-testid="mail-empty">Nothing in quarantine. Unrecognised senders will land here.</div>
        )
      )}

      <div className="space-y-2" data-testid="mail-list">
        {items.map((q) => (
          <MailRow key={q.id} q={q} busy={busyId === q.id}
            onConvert={() => setConvertFor(q)}
            onAttach={() => setAttachFor(q)}
            onAddContact={() => setContactFor(q)}
            onDiscard={() => discard(q)} />
        ))}
      </div>

      {convertFor && (
        <MailClientDialog
          title="Create ticket"
          description={`Which client is ${mailSenderLabel(convertFor)}?`}
          confirmLabel="Create ticket"
          onClose={() => setConvertFor(null)}
          onSubmit={(clientId) => api.post(`/mail/quarantine/${convertFor.id}/convert`, { client_id: clientId })}
          // `cancelled` is true only when Cancel was clicked WHILE the POST
          // was in flight -- the request already reached the server and
          // cannot be recalled, so the row really is resolved and `settle`
          // still runs, but the user who said no does not get NAVIGATED to
          // a ticket they just backed out of. The toast still says the
          // truth rather than nothing at all.
          onDone={(data, cancelled) => {
            settle(convertFor.id);
            setConvertFor(null);
            if (cancelled) {
              toast(`Cancelled, but the ticket had already been created (#${data?.ticket_id || "?"}). It was not opened for you.`);
              return;
            }
            toast.success("Ticket created");
            if (data?.ticket_id) nav(`/tickets/${data.ticket_id}`);
          }}
        />
      )}

      {attachFor && (
        <MailAttachDialog
          quarantine={attachFor}
          onClose={() => setAttachFor(null)}
          onDone={(data, cancelled) => {
            settle(attachFor.id);
            setAttachFor(null);
            if (cancelled) {
              toast(`Cancelled, but the mail had already been attached to ticket #${data?.ticket_id || "?"}.`);
              return;
            }
            toast.success("Attached to ticket");
          }}
        />
      )}

      {contactFor && (
        <MailClientDialog
          title="Add sender as contact"
          description={`Register ${contactFor.from_addr || "this sender"} against a client and raise a ticket.`}
          confirmLabel="Add contact & create ticket"
          nameField
          defaultName={contactFor.from_name || ""}
          onClose={() => setContactFor(null)}
          onSubmit={(clientId, name) => api.post(`/mail/quarantine/${contactFor.id}/add-contact`, { client_id: clientId, name: name || undefined })}
          onDone={(data, cancelled) => {
            settle(contactFor.id);
            setContactFor(null);
            if (cancelled) {
              toast(`Cancelled, but the contact was already added${data?.contact_email ? ` (${data.contact_email})` : ""} and a ticket created (#${data?.ticket_id || "?"}). It was not opened for you.`);
              return;
            }
            toast.success(`Contact added${data?.contact_email ? ` (${data.contact_email})` : ""}`);
            if (data?.ticket_id) nav(`/tickets/${data.ticket_id}`);
          }}
        />
      )}
    </div>
  );
}

function MailRow({ q, busy, onConvert, onAttach, onAddContact, onDiscard }) {
  const attCount = Array.isArray(q.attachments) ? q.attachments.length : 0;
  const hasHtml = !!(q.html && String(q.html).trim());

  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3" data-testid={`mail-row-${q.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-zinc-100 font-medium truncate" data-testid={`mail-sender-${q.id}`}>{mailSenderLabel(q)}</div>
          <div className="text-sm text-zinc-300 truncate mt-0.5" data-testid={`mail-subject-${q.id}`}>
            {q.subject?.trim() || "(no subject)"}
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-zinc-500 shrink-0">
          <span data-testid={`mail-received-${q.id}`}>{q.received_at ? fmtTime(q.received_at) : "—"}</span>
          <span className="flex items-center gap-1" title={`${attCount} attachment${attCount === 1 ? "" : "s"}`}
            data-testid={`mail-attcount-${q.id}`}>
            <Paperclip size={12} /> {attCount}
          </span>
          <span data-testid={`mail-age-${q.id}`}><MailAge receivedAt={q.received_at} businessHoursOld={q.business_hours_old} /></span>
        </div>
      </div>

      {/* The single sanitised render path: HTML is passed through
          renderUntrustedHtml (DOMPurify only, no markdown parsing); a
          plain-text body is a React text node, never innerHTML, so it
          cannot be anything but escaped. */}
      <div className="border border-zinc-900 rounded bg-zinc-900/30 p-3 max-h-40 overflow-y-auto text-sm text-zinc-300"
        data-testid={`mail-preview-${q.id}`}>
        {hasHtml ? (
          <div data-testid={`mail-preview-html-${q.id}`}
            dangerouslySetInnerHTML={{ __html: renderUntrustedHtml(q.html) }} />
        ) : (
          <pre className="whitespace-pre-wrap font-sans" data-testid={`mail-preview-text-${q.id}`}>{q.text || "(empty message)"}</pre>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={onConvert} disabled={busy} data-testid={`mail-convert-${q.id}`}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-3 py-1.5 text-xs flex items-center gap-1.5">
          <Plus size={13} /> Create ticket
        </button>
        <button onClick={onAttach} disabled={busy} data-testid={`mail-attach-${q.id}`}
          className="border border-zinc-800 hover:border-zinc-700 disabled:opacity-50 text-zinc-200 rounded px-3 py-1.5 text-xs flex items-center gap-1.5">
          <Paperclip size={13} /> Attach to existing
        </button>
        <button onClick={onAddContact} disabled={busy} data-testid={`mail-addcontact-${q.id}`}
          className="border border-zinc-800 hover:border-zinc-700 disabled:opacity-50 text-zinc-200 rounded px-3 py-1.5 text-xs flex items-center gap-1.5">
          <UserPlus size={13} /> Add sender as contact
        </button>
        <button onClick={onDiscard} disabled={busy} data-testid={`mail-discard-${q.id}`}
          className="ml-auto text-red-400 hover:text-red-300 disabled:opacity-50 rounded px-3 py-1.5 text-xs flex items-center gap-1.5">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Discard
        </button>
      </div>
    </div>
  );
}

// Shared by Convert and Add-sender-as-contact: both ask "which client", and
// add-contact additionally asks for a name to register. `onSubmit` returns
// the axios promise; this dialog awaits it and hands the response body to
// `onDone` so the caller (which knows whether this created a ticket to land
// on) decides what happens next.
function MailClientDialog({ title, description, confirmLabel, nameField, defaultName, onClose, onSubmit, onDone }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [name, setName] = useState(defaultName || "");
  const [busy, setBusy] = useState(false);
  // Cancel cannot un-send a request already in flight -- the server will
  // still act on it. What it CAN do is stop this dialog pretending the user
  // confirmed: the eventual result is reported to `onDone` as `cancelled`,
  // so the CALLER settles the row (the server really did resolve it) but
  // skips navigating the user to a ticket they just said no to, and shows an
  // honest toast instead of the normal success one. A plain variable would
  // not survive the unmount `onClose()` triggers; a ref does.
  const cancelledRef = useRef(false);

  useEffect(() => { api.get("/clients").then((r) => setClients(asArray(r.data))).catch(() => setClients([])); }, []);

  const submit = async () => {
    if (!clientId) { toast.error("Pick a client."); return; }
    setBusy(true);
    try {
      const { data } = await onSubmit(clientId, name.trim());
      onDone(data, cancelledRef.current);
    } catch (e) {
      // If the user already cancelled, the failure changes nothing they can
      // act on here -- the dialog is gone and nothing was created. Surface
      // it only while they are still looking at this dialog.
      if (!cancelledRef.current) toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    if (busy) cancelledRef.current = true;
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent data-testid="mail-client-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription className="text-zinc-500">{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <span className={labelCls}>Client</span>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
              data-testid="mail-client-select" className={inputCls} autoFocus>
              <option value="">Select a client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {nameField && (
            <label className="block">
              <span className={labelCls}>Contact name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="mail-contact-name" className={inputCls} />
            </label>
          )}
        </div>
        <DialogFooter>
          <button onClick={cancel} data-testid="mail-client-cancel" className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="mail-client-confirm"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm flex items-center gap-1.5">
            {busy && <Loader2 size={14} className="animate-spin" />} {confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MailAttachDialog({ quarantine, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [ticketId, setTicketId] = useState("");
  const [busy, setBusy] = useState(false);
  // See the matching comment in MailClientDialog -- same reasoning, same shape.
  const cancelledRef = useRef(false);

  useEffect(() => {
    const text = q.trim();
    if (!text) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/tickets", { params: { q: text, limit: 10 } })
        .then((r) => setResults(asArray(r.data?.items ?? r.data)))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async () => {
    if (!ticketId) { toast.error("Pick a ticket."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/mail/quarantine/${quarantine.id}/attach`, { ticket_id: ticketId });
      onDone(data, cancelledRef.current);
    } catch (e) {
      if (!cancelledRef.current) toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    if (busy) cancelledRef.current = true;
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent data-testid="mail-attach-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Attach to existing ticket</DialogTitle>
          <DialogDescription className="text-zinc-500">Search by title, then pick one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <span className={labelCls}>Ticket</span>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input value={q} onChange={(e) => { setQ(e.target.value); setTicketId(""); }}
                placeholder="Search ticket titles…" data-testid="mail-attach-search"
                className={inputCls + " pl-8"} autoFocus />
            </div>
          </label>
          <div className="max-h-48 overflow-y-auto border border-zinc-800 rounded divide-y divide-zinc-900" data-testid="mail-attach-results">
            {searching && <div className="text-xs text-zinc-600 p-2">Searching…</div>}
            {!searching && q.trim() && !results.length && <div className="text-xs text-zinc-600 p-2">No matching tickets.</div>}
            {results.map((t) => (
              <button key={t.id} type="button" onClick={() => setTicketId(t.id)} data-testid={`mail-attach-result-${t.id}`}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-900 ${ticketId === t.id ? "bg-blue-500/10 text-blue-300" : "text-zinc-300"}`}>
                <div className="truncate">{t.title || "(untitled)"}</div>
                <div className="text-[11px] text-zinc-600">#{t.number ?? t.id}</div>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <button onClick={cancel} data-testid="mail-attach-cancel" className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
          <button onClick={submit} disabled={busy || !ticketId} data-testid="mail-attach-confirm"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm flex items-center gap-1.5">
            {busy && <Loader2 size={14} className="animate-spin" />} Attach
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------- //
// SLA
// --------------------------------------------------------------------------- //
// A compound select value, so ONE dropdown can name three different kinds
// of scope without a second control: "" (tenant default), "tier:Premium"
// (a tier), "client:cli_xxx" (one client's own override). Parsed back into
// {clientId, tier} -- never both -- immediately below.
const parseScope = (v) => {
  if (v.startsWith("tier:")) return { clientId: "", tier: v.slice(5) };
  if (v.startsWith("client:")) return { clientId: v.slice(7), tier: "" };
  return { clientId: "", tier: "" };
};

function SlaTab() {
  const [clients, setClients] = useState([]);
  const [scope, setScope] = useState("");
  const { clientId, tier } = parseScope(scope);
  const [data, setData] = useState(null);
  const [name, setName] = useState("Standard service level");
  const [targets, setTargets] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [holiday, setHoliday] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(asArray(r.data))).catch(() => setClients([]));
  }, []);

  const load = useCallback((cid, t) => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (cid) qs.set("client_id", cid);
    if (t) qs.set("tier", t);
    api.get(`/desk/sla?${qs}`).then((r) => {
      setData(r.data);
      setTargets(r.data.targets);
      setCalendar(r.data.calendar);
      setName(r.data.targets?.critical?.policy_name || "Standard service level");
      setErr("");
    }).catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(clientId, tier); }, [clientId, tier, load]);

  const setTarget = (p, field, val) => setTargets((t) => ({ ...t, [p]: { ...t[p], [field]: val } }));
  const toggleDay = (d) => setCalendar((c) => {
    const days = c.days_of_week.includes(d) ? c.days_of_week.filter((x) => x !== d) : [...c.days_of_week, d].sort();
    return { ...c, days_of_week: days };
  });
  const addHoliday = () => {
    if (!holiday) return;
    if (calendar.holidays.includes(holiday)) { setHoliday(""); return; }
    setCalendar((c) => ({ ...c, holidays: [...c.holidays, holiday].sort() }));
    setHoliday("");
  };
  const removeHoliday = (h) => setCalendar((c) => ({ ...c, holidays: c.holidays.filter((x) => x !== h) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        client_id: clientId || null,
        tier: tier || null,
        name,
        targets: Object.fromEntries(PRIORITIES.map((p) => [p, {
          response_minutes: Number(targets[p].response_minutes) || 0,
          resolution_minutes: Number(targets[p].resolution_minutes) || 0,
        }])),
        calendar: {
          days_of_week: calendar.days_of_week,
          start_hour: Number(calendar.start_hour),
          end_hour: Number(calendar.end_hour),
          tz_offset_minutes: Number(calendar.tz_offset_minutes),
          always_on: !!calendar.always_on,
          holidays: calendar.holidays,
        },
      };
      const { data: fresh } = await api.put("/desk/sla", payload);
      setData(fresh);
      setTargets(fresh.targets);
      setCalendar(fresh.calendar);
      toast.success(tier ? `${tier} tier service level saved`
        : clientId ? "Client service level saved" : "Tenant default service level saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const source = data?.targets?.critical?.source;
  const sourceLabel = source === "client" ? "Client override" : source === "tier" ? "Tier default"
    : source === "tenant default" ? "Tenant default" : "Built-in default";
  const sourceTone = source === "client" ? "text-blue-300 border-blue-500/30 bg-blue-500/10"
    : source === "tier" ? "text-violet-300 border-violet-500/30 bg-violet-500/10"
    : source === "tenant default" ? "text-zinc-300 border-zinc-700 bg-zinc-800/60"
    : "text-amber-300 border-amber-500/30 bg-amber-500/10";

  return (
    <div className="space-y-4" data-testid="sla-tab">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block min-w-[240px]">
          <span className={labelCls}>Scope</span>
          <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="sla-scope-select"
            className={inputCls}>
            <option value="">— Tenant default —</option>
            <optgroup label="Tiers (every client at that tier)">
              {SLA_TIERS.map((t) => <option key={t} value={`tier:${t}`}>{t}</option>)}
            </optgroup>
            <optgroup label="Individual clients (overrides their tier)">
              {clients.map((c) => (
                <option key={c.id} value={`client:${c.id}`}>
                  {c.name}{c.tier ? ` (${c.tier})` : ""}
                </option>
              ))}
            </optgroup>
          </select>
          {clientId && (() => {
            const c = clients.find((x) => x.id === clientId);
            return c?.tier ? (
              <span className="text-[11px] text-zinc-500 mt-1 block">
                Falls back to the <span className="text-zinc-300">{c.tier}</span> tier's policy if this client has none of its own.
              </span>
            ) : null;
          })()}
        </label>
        {!loading && !err && (
          <span data-testid="sla-source" className={`text-[11px] uppercase tracking-wider font-mono px-2 py-1 rounded border ${sourceTone}`}>
            Currently showing: {sourceLabel}
          </span>
        )}
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>}
      {loading && <div className="text-zinc-600 text-sm">Loading…</div>}

      {!loading && !err && targets && calendar && (
        <>
          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3">
            <label className="block max-w-md">
              <span className={labelCls}>Policy name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="sla-policy-name" className={inputCls} />
            </label>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {PRIORITIES.map((p) => (
                <div key={p} className="border border-zinc-900 rounded p-3 space-y-2" data-testid={`sla-target-${p}`}>
                  <div className="text-xs uppercase tracking-wider font-mono text-zinc-400">{p}</div>
                  <label className="block">
                    <span className="text-[11px] text-zinc-500">Response (min)</span>
                    <input type="number" min={1} value={targets[p].response_minutes}
                      onChange={(e) => setTarget(p, "response_minutes", e.target.value)}
                      data-testid={`sla-${p}-response`} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="text-[11px] text-zinc-500">Resolution (min)</span>
                    <input type="number" min={1} value={targets[p].resolution_minutes}
                      onChange={(e) => setTarget(p, "resolution_minutes", e.target.value)}
                      data-testid={`sla-${p}-resolution`} className={inputCls} />
                  </label>
                  <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-600">source: {targets[p].source}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3">
            <div className="font-display font-medium text-sm text-zinc-200">Business calendar</div>
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" className="accent-blue-500" checked={!!calendar.always_on}
                onChange={(e) => setCalendar((c) => ({ ...c, always_on: e.target.checked }))} data-testid="sla-always-on" />
              Always on (24/7 — ignores working days and hours)
            </label>
            {!calendar.always_on && (
              <>
                <div>
                  <span className={labelCls}>Working days</span>
                  <div className="flex gap-1 flex-wrap">
                    {DAYS.map((d, i) => (
                      <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`sla-day-${i}`}
                        className={`px-2.5 py-1 rounded text-xs border ${calendar.days_of_week.includes(i)
                          ? "bg-blue-500/15 border-blue-500/40 text-blue-300" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-xl">
                  <label className="block">
                    <span className={labelCls}>Start hour (0-23)</span>
                    <input type="number" min={0} max={23} value={calendar.start_hour}
                      onChange={(e) => setCalendar((c) => ({ ...c, start_hour: e.target.value }))}
                      data-testid="sla-start-hour" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>End hour (0-23)</span>
                    <input type="number" min={0} max={23} value={calendar.end_hour}
                      onChange={(e) => setCalendar((c) => ({ ...c, end_hour: e.target.value }))}
                      data-testid="sla-end-hour" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>TZ offset (minutes)</span>
                    <input type="number" value={calendar.tz_offset_minutes}
                      onChange={(e) => setCalendar((c) => ({ ...c, tz_offset_minutes: e.target.value }))}
                      data-testid="sla-tz-offset" className={inputCls} />
                  </label>
                </div>
                <div>
                  <span className={labelCls}>Holidays (ISO dates)</span>
                  <div className="flex gap-2 max-w-sm">
                    <input type="date" value={holiday} onChange={(e) => setHoliday(e.target.value)}
                      data-testid="sla-holiday-input" className={inputCls} />
                    <button onClick={addHoliday} type="button" data-testid="sla-holiday-add"
                      className="shrink-0 bg-zinc-900 border border-zinc-800 rounded px-3 text-sm text-zinc-200">Add</button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {calendar.holidays.map((h) => (
                      <span key={h} className="flex items-center gap-1 text-[11px] font-mono bg-zinc-900 border border-zinc-800 rounded px-2 py-1">
                        {h}
                        <button onClick={() => removeHoliday(h)} className="text-zinc-500 hover:text-red-400"><X size={11} /></button>
                      </span>
                    ))}
                    {!calendar.holidays.length && <span className="text-xs text-zinc-600">None</span>}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end">
            <button onClick={save} disabled={saving} data-testid="sla-save"
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm flex items-center gap-1.5">
              <Save size={14} /> {saving ? "Saving…" : (clientId ? "Save client override" : "Save tenant default")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Templates
// --------------------------------------------------------------------------- //
const EMPTY_TEMPLATE = { name: "", category: "general", priority: "medium", tasksText: "" };

function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // template being edited, or {} for new
  const [form, setForm] = useState(EMPTY_TEMPLATE);
  const [raising, setRaising] = useState(null); // template id
  const [raiseClient, setRaiseClient] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/desk/templates").then((r) => { setTemplates(asArray(r.data)); setErr(""); })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/clients").then((r) => setClients(asArray(r.data))).catch(() => setClients([])); }, []);

  const startNew = () => { setEditing({}); setForm(EMPTY_TEMPLATE); };
  const startEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, category: t.category || "general", priority: t.priority || "medium",
               tasksText: (t.tasks || []).join("\n") });
  };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY_TEMPLATE); };

  const save = async () => {
    const payload = {
      name: form.name.trim(),
      category: form.category.trim() || "general",
      priority: form.priority,
      tasks: form.tasksText.split("\n").map((t) => t.trim()).filter(Boolean),
    };
    if (!payload.name) { toast.error("Name is required."); return; }
    try {
      if (editing?.id) await api.put(`/desk/templates/${editing.id}`, payload);
      else await api.post("/desk/templates", payload);
      toast.success(editing?.id ? "Template updated" : "Template created");
      cancelEdit();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (t) => {
    const msg = t.key
      ? `Delete built-in template "${t.name}"?\n\nThis resets it to the default — the next time anyone opens Templates, it comes back with its original tasks.`
      : `Delete template "${t.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/desk/templates/${t.id}`);
      toast.success(t.key ? "Deleted — will reappear as the built-in default" : "Template deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const raise = async (t) => {
    try {
      const { data } = await api.post(`/desk/templates/${t.id}/raise`, { client_id: raiseClient || undefined });
      toast.success(`Ticket #${data.number} raised from "${t.name}"`);
      setRaising(null); setRaiseClient("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="templates-tab">
      <div className="flex justify-end">
        <button onClick={startNew} data-testid="template-create-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5">
          <Plus size={15} /> New template
        </button>
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>}
      {loading && <div className="text-zinc-600 text-sm">Loading…</div>}
      {!loading && !err && !templates.length && <div className="text-zinc-600 text-sm">No templates yet.</div>}

      <div className="grid gap-3 md:grid-cols-2" data-testid="template-list">
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-row-${t.id}`} className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-zinc-100 font-medium truncate">{t.name}</span>
                  {t.key && (
                    <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border bg-amber-500/10 text-amber-300 border-amber-500/30">Built-in</span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">{t.category} · {t.priority} · {t.tasks?.length || 0} tasks</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => startEdit(t)} title="Edit" data-testid={`template-edit-${t.id}`}
                  className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"><Pencil size={14} /></button>
                <button onClick={() => remove(t)} title="Delete" data-testid={`template-delete-${t.id}`}
                  className="p-1.5 rounded hover:bg-zinc-800 text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
            {t.key && (
              <div className="text-[11px] text-amber-400/80">
                This is a built-in template. Deleting it resets it to the default — it is re-created automatically the next time this list loads.
              </div>
            )}
            {raising === t.id ? (
              <div className="flex gap-2 items-center pt-1">
                <select value={raiseClient} onChange={(e) => setRaiseClient(e.target.value)}
                  data-testid={`template-raise-client-${t.id}`} className="flex-1 min-w-0 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs">
                  <option value="">— no client —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button onClick={() => raise(t)} data-testid={`template-raise-confirm-${t.id}`}
                  className="text-xs bg-blue-600 hover:bg-blue-700 text-white rounded px-2.5 py-1">Raise</button>
                <button onClick={() => setRaising(null)} className="text-xs text-zinc-500 hover:text-zinc-300">Cancel</button>
              </div>
            ) : (
              <button onClick={() => setRaising(t.id)} data-testid={`template-raise-${t.id}`}
                className="text-xs border border-zinc-800 rounded px-2.5 py-1 text-zinc-300 hover:bg-zinc-900">Raise a ticket</button>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <Dialog open onOpenChange={(o) => { if (!o) cancelEdit(); }}>
          <DialogContent data-testid="template-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">{editing.id ? "Edit template" : "New template"}</DialogTitle>
              <DialogDescription className="sr-only">Author a ticket template with a checklist.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="block">
                <span className={labelCls}>Name</span>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="template-name" className={inputCls} autoFocus />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelCls}>Category</span>
                  <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    data-testid="template-category" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Priority</span>
                  <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                    data-testid="template-priority" className={inputCls}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className={labelCls}>Tasks (one per line)</span>
                <textarea value={form.tasksText} onChange={(e) => setForm((f) => ({ ...f, tasksText: e.target.value }))}
                  rows={7} data-testid="template-tasks" className={inputCls + " font-mono"} />
              </label>
            </div>
            <DialogFooter>
              <button onClick={cancelEdit} className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
              <button onClick={save} data-testid="template-save"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded px-4 py-1.5 text-sm flex items-center gap-1.5">
                <Save size={14} /> Save
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Work types & billing settings
// --------------------------------------------------------------------------- //
// The local `peso()` that used to live here is gone. It was byte-identical to
// TimeApproval.jsx's and both used `minimumFractionDigits: 0` with no maximum,
// so a rate of ₱1,234.50 rendered as "₱1,234.5". One formatter now, taking
// integer centavos: `frontend/src/lib/money.js`. `rate_minor` comes from
// `timetracking.rate_for` beside the major-unit `rate`, so the browser is not
// multiplying a float by 100 to reach it.

function WorkTypesTab() {
  const [workTypes, setWorkTypes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/desk/work-types").then((r) => {
      setWorkTypes(asArray(r.data?.work_types));
      setSettings(r.data?.settings || null);
      setErr("");
    }).catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/desk/billing-settings", {
        increment_minutes: Number(settings.increment_minutes) || 1,
        minimum_minutes: Number(settings.minimum_minutes) || 0,
        currency: settings.currency || "PHP",
        require_approval: !!settings.require_approval,
      });
      setSettings(data);
      toast.success("Billing settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="worktypes-tab">
      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>}
      {loading && <div className="text-zinc-600 text-sm">Loading…</div>}

      {!loading && !err && (
        <>
          <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
            <table className="w-full text-sm hidden sm:table">
              <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-4 py-3">Work type</th>
                  <th className="text-left px-4 py-3">Rate / hour (PHP)</th>
                  <th className="text-left px-4 py-3">Billable</th>
                  <th className="text-left px-4 py-3">Source</th>
                </tr>
              </thead>
              <tbody>
                {workTypes.map((w) => (
                  <tr key={w.id || w.key} className="border-b border-zinc-900" data-testid={`worktype-row-${w.key}`}>
                    <td className="px-4 py-2 text-zinc-200">{w.name}</td>
                    <td className="px-4 py-2 font-mono text-zinc-100">{pesoOr(w.rate_minor, "No rate recorded")}</td>
                    <td className="px-4 py-2 text-zinc-400">{w.billable ? "Billable" : "Non-chargeable"}</td>
                    <td className="px-4 py-2 text-zinc-500 text-xs">{w.rate_source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Mobile: labeled cards instead of a horizontally-scrolling table. */}
            <div className="sm:hidden divide-y divide-zinc-900">
              {workTypes.map((w) => (
                <div key={w.id || w.key} className="p-4 space-y-1" data-testid={`worktype-card-${w.key}`}>
                  <div className="text-zinc-100 font-medium">{w.name}</div>
                  <div className="text-xs text-zinc-500">Rate / hour: <span className="font-mono text-zinc-200">{pesoOr(w.rate_minor, "No rate recorded")}</span></div>
                  <div className="text-xs text-zinc-500">{w.billable ? "Billable" : "Non-chargeable"} · {w.rate_source}</div>
                </div>
              ))}
            </div>
            {!workTypes.length && <div className="px-4 py-6 text-zinc-600 text-sm">No work types configured.</div>}
          </div>

          {settings && (
            <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3 max-w-xl">
              <div className="font-display font-medium text-sm text-zinc-200">Billing settings</div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelCls}>Round up to (minutes)</span>
                  <input type="number" min={1} value={settings.increment_minutes}
                    onChange={(e) => setSettings((s) => ({ ...s, increment_minutes: e.target.value }))}
                    data-testid="billing-increment" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Minimum (minutes)</span>
                  <input type="number" min={0} value={settings.minimum_minutes}
                    onChange={(e) => setSettings((s) => ({ ...s, minimum_minutes: e.target.value }))}
                    data-testid="billing-minimum" className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Currency</span>
                  <input value={settings.currency}
                    onChange={(e) => setSettings((s) => ({ ...s, currency: e.target.value }))}
                    data-testid="billing-currency" className={inputCls} />
                </label>
                <label className="flex items-center gap-2 text-sm text-zinc-300 mt-5">
                  <input type="checkbox" className="accent-blue-500" checked={!!settings.require_approval}
                    onChange={(e) => setSettings((s) => ({ ...s, require_approval: e.target.checked }))}
                    data-testid="billing-require-approval" />
                  Require approval before invoicing
                </label>
              </div>
              <div className="flex justify-end">
                <button onClick={save} disabled={saving} data-testid="billing-save"
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm flex items-center gap-1.5">
                  <Save size={14} /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Canned responses
// --------------------------------------------------------------------------- //
const EMPTY_CANNED = { title: "", body_md: "", shared: false };

function CannedResponsesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [me, setMe] = useState(null);
  const [dialog, setDialog] = useState(null); // { editing: snippet|null, form }

  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  const mayManageShared = !!me && MANAGE_SHARED_ROLES.includes(me.role);

  const load = useCallback(() => {
    setLoading(true);
    // The endpoint itself scopes to shared + the caller's own -- never
    // request or render another technician's personal snippets here.
    api.get("/canned-responses", { params: { limit: 200 } })
      .then((r) => { setItems(asArray(r.data?.items)); setErr(""); })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const shared = items.filter((i) => i.owner_id === null || i.owner_id === undefined);
  const mine = items.filter((i) => i.owner_id != null);

  const openCreate = () => setDialog({ editing: null, form: { ...EMPTY_CANNED, shared: false } });
  const openEdit = (s) => setDialog({
    editing: s,
    form: { title: s.title, body_md: s.body_md, shared: s.owner_id === null || s.owner_id === undefined },
  });

  const save = async () => {
    const { editing, form } = dialog;
    const title = form.title.trim();
    if (!title) { toast.error("Title is required."); return; }
    if (!form.body_md.trim()) { toast.error("Body is required."); return; }
    try {
      if (editing) {
        // PATCH only ever carries title/body_md -- whether a snippet is
        // shared is fixed at creation and not editable here.
        await api.patch(`/canned-responses/${editing.id}`, { title, body_md: form.body_md });
      } else {
        await api.post("/canned-responses", { title, body_md: form.body_md, shared: !!form.shared });
      }
      toast.success(editing ? "Canned response updated" : "Canned response created");
      setDialog(null);
      load();
    } catch (e) {
      // Surfaced verbatim: a plain technician trying to share gets the
      // server's own 403 here, not a silently-ignored checkbox.
      toast.error(formatApiError(e));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete canned response "${s.title}"?`)) return;
    try {
      await api.delete(`/canned-responses/${s.id}`);
      toast.success("Canned response deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const Row = ({ s, isShared }) => (
    <div key={s.id} data-testid={`canned-row-${s.id}`}
      className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-zinc-100 font-medium truncate">{s.title}</span>
            {isShared && (
              <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border bg-blue-500/10 text-blue-300 border-blue-500/30">Shared</span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1 truncate">{(s.body_md || "").split("\n")[0]}</div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={() => openEdit(s)} title="Edit" data-testid={`canned-edit-${s.id}`}
            className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"><Pencil size={14} /></button>
          <button onClick={() => remove(s)} title="Delete" data-testid={`canned-delete-${s.id}`}
            className="p-1.5 rounded hover:bg-zinc-800 text-red-400"><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="canned-tab">
      <div className="flex justify-end">
        <button onClick={openCreate} data-testid="canned-create-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5">
          <Plus size={15} /> New canned response
        </button>
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>}
      {loading && <div className="text-zinc-600 text-sm">Loading…</div>}

      {!loading && !err && (
        <>
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Shared</div>
            {!shared.length && <div className="text-zinc-600 text-sm">No shared canned responses yet.</div>}
            <div className="grid gap-3 md:grid-cols-2" data-testid="canned-shared-list">
              {shared.map((s) => <Row key={s.id} s={s} isShared />)}
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Mine</div>
            {!mine.length && <div className="text-zinc-600 text-sm">You have no personal canned responses yet.</div>}
            <div className="grid gap-3 md:grid-cols-2" data-testid="canned-mine-list">
              {mine.map((s) => <Row key={s.id} s={s} isShared={false} />)}
            </div>
          </div>
        </>
      )}

      {dialog && (
        <Dialog open onOpenChange={(o) => { if (!o) setDialog(null); }}>
          <DialogContent data-testid="canned-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">{dialog.editing ? "Edit canned response" : "New canned response"}</DialogTitle>
              <DialogDescription className="sr-only">Author a reusable reply with ticket variables.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="block">
                <span className={labelCls}>Title</span>
                <input value={dialog.form.title} data-testid="canned-title"
                  onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, title: e.target.value } }))}
                  className={inputCls} autoFocus />
              </label>
              <label className="block">
                <span className={labelCls}>Body (Markdown -- {"{{ticket.number}}"}, {"{{ticket.title}}"}, {"{{ticket.status}}"}, {"{{contact.name}}"}, {"{{client.name}}"}, {"{{agent.name}}"})</span>
                <textarea value={dialog.form.body_md} data-testid="canned-body"
                  onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, body_md: e.target.value } }))}
                  rows={7} className={inputCls + " font-mono"} />
              </label>
              {!dialog.editing && (
                <div title={mayManageShared ? undefined : "Only a service desk manager, L3 technician, MSP owner or super admin can share a canned response."}>
                  <span className={labelCls}>Visibility</span>
                  <div className="flex gap-1">
                    {[[false, "Personal — only me"], [true, "Shared — the whole desk"]].map(([v, label]) => (
                      <button key={String(v)} type="button"
                        disabled={v && !mayManageShared}
                        data-testid={v ? "canned-shared-toggle" : "canned-personal-toggle"}
                        onClick={() => setDialog((d) => ({ ...d, form: { ...d.form, shared: v } }))}
                        className={`px-3 py-1 text-xs rounded border disabled:opacity-40 disabled:cursor-not-allowed ${dialog.form.shared === v
                          ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
                          : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {!mayManageShared && (
                    <div className="text-[11px] text-zinc-600 mt-1">
                      Your role can only create personal canned responses. The server enforces this independently.
                    </div>
                  )}
                </div>
              )}
              {dialog.editing && (
                <div className="text-[11px] text-zinc-600">
                  Visibility is set at creation and cannot be changed here.
                </div>
              )}
            </div>
            <DialogFooter>
              <button onClick={() => setDialog(null)} className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
              <button onClick={save} data-testid="canned-save"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded px-4 py-1.5 text-sm flex items-center gap-1.5">
                <Save size={14} /> Save
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// VIP contacts
//
// VIP-ness lives on the EMAIL (`backend/requesters.annotate_vip` reads it on
// every ticket read, live -- never stamped onto a ticket at creation). One
// flat list rather than a separate "tag a user" mechanism: a portal account
// is reachable by its own email exactly the way a phone-in caller who has
// never registered is, so Users.jsx's own VIP toggle (that page's per-row
// action) posts to this SAME endpoint with that account's email.
// --------------------------------------------------------------------------- //
function VipTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/vip-contacts").then((r) => setRows(asArray(r.data)))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email.trim()) { toast.error("An email is required."); return; }
    setSaving(true);
    try {
      await api.post("/vip-contacts", {
        email: email.trim(), name: name.trim() || undefined, note: note.trim() || undefined,
      });
      setEmail(""); setName(""); setNote("");
      toast.success("Tagged VIP");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove the VIP tag from ${row.email}?`)) return;
    try {
      await api.delete(`/vip-contacts/${row.id}`);
      toast.success("VIP tag removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="vip-tab">
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3">
        <div className="text-sm font-medium text-zinc-200 flex items-center gap-1.5">
          <Star size={14} className="text-amber-400" /> Tag an email as VIP
        </div>
        <p className="text-xs text-zinc-500">
          Any ticket raised by this email -- whether from a portal account or typed into a
          ticket's contact details -- shows a VIP badge, on the ticket list and its detail
          page, for every past and future ticket from it.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className={labelCls}>Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
              data-testid="vip-add-email" placeholder="person@client.com" className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Name (optional)</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="vip-add-name" className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="vip-add-note" placeholder="Why this person is VIP" className={inputCls} />
          </label>
        </div>
        <button onClick={add} disabled={saving} data-testid="vip-add-submit"
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm">
          <Plus size={14} /> {saving ? "Adding…" : "Add"}
        </button>
      </div>

      {loading && <div className="text-sm text-zinc-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-sm text-zinc-500" data-testid="vip-empty">No VIP contacts tagged yet.</div>
      )}
      {!loading && rows.length > 0 && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <tr>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Note</th>
                <th className="text-left px-4 py-2.5">Tagged</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-zinc-900" data-testid={`vip-row-${r.id}`}>
                  <td className="px-4 py-2 font-mono text-zinc-200 flex items-center gap-1.5">
                    <Star size={12} className="text-amber-400 shrink-0" /> {r.email}
                  </td>
                  <td className="px-4 py-2 text-zinc-400">{r.name || "—"}</td>
                  <td className="px-4 py-2 text-zinc-500">{r.note || "—"}</td>
                  <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">{fmtTime(r.created_at)}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => remove(r)} data-testid={`vip-remove-${r.id}`}
                      className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
